import { gql } from "@apollo/client";
import client from "../apolloClient";
import {
  ForgotPasswordRequestData,
  LoginRequestData,
  ResetPasswordRequestData,
  VerifyOtpRequestData,
} from "../../schemas/auth/requestData";
import {
  LoginResponse,
  ForgotPasswordResponse,
  VerifyOtpResponse,
  User,
} from "../../types/auth";

const GET_PROFILE = gql`
  query GetProfile {
    profile {
      id
      name
      username
      email
      tenantId
      roleId
      role
      phoneNumber
      createdAt
      updatedAt
      permissions
    }
  }
`;

const LOGIN = gql`
  mutation Login($username: String!, $password: String!) {
    login(username: $username, password: $password) {
      accessToken
      refreshToken
      user {
        id
        name
        username
        email
        tenantId
        roleId
        role
        phoneNumber
        createdAt
        updatedAt
        permissions
      }
    }
  }
`;

const FORGOT_PASSWORD = gql`
  mutation ForgotPassword($email: String!) {
    forgotPassword(email: $email) {
      message
      expiresIn
    }
  }
`;

const VERIFY_OTP = gql`
  mutation VerifyOtp($email: String!, $otp: String!) {
    verifyOtp(email: $email, otp: $otp) {
      message
      userId
    }
  }
`;

const RESET_PASSWORD = gql`
  mutation ResetPassword($email: String!, $newPassword: String!) {
    resetPassword(email: $email, newPassword: $newPassword)
  }
`;

const EDIT_PROFILE = gql`
  mutation EditProfile($input: EditProfileInput!) {
    editProfile(input: $input) {
      id
      name
      username
      email
      tenantId
      roleId
      role
      phoneNumber
      createdAt
      updatedAt
      permissions
    }
  }
`;

const CHANGE_PASSWORD = gql`
  mutation ChangePassword($currentPassword: String!, $newPassword: String!) {
    changePassword(currentPassword: $currentPassword, newPassword: $newPassword)
  }
`;

const LOGOUT = gql`
  mutation Logout($refreshToken: String!) {
    logout(refreshToken: $refreshToken)
  }
`;

export const authService = {
  login: async (data: LoginRequestData): Promise<LoginResponse> => {
    const { data: response } = await client.mutate({
      mutation: LOGIN,
      variables: {
        username: data.username,
        password: data.password,
      },
    });
    return response.login;
  },

  forgotPassword: async (
    data: ForgotPasswordRequestData,
  ): Promise<ForgotPasswordResponse> => {
    const { data: response } = await client.mutate({
      mutation: FORGOT_PASSWORD,
      variables: { email: data.email },
    });
    return response.forgotPassword;
  },

  verifyOtp: async (data: VerifyOtpRequestData): Promise<VerifyOtpResponse> => {
    const { data: response } = await client.mutate({
      mutation: VERIFY_OTP,
      variables: {
        email: data.email,
        otp: data.otp,
      },
    });
    return response.verifyOtp;
  },

  resetPassword: async (data: ResetPasswordRequestData): Promise<boolean> => {
    const { data: response } = await client.mutate({
      mutation: RESET_PASSWORD,
      variables: {
        email: data.email,
        newPassword: data.newPassword,
      },
    });
    return response.resetPassword;
  },

  logout: async (): Promise<boolean> => {
    const refreshToken = localStorage.getItem("refreshToken");
    if (!refreshToken) return true;
    const { data: response } = await client.mutate({
      mutation: LOGOUT,
      variables: { refreshToken },
    });
    return response.logout;
  },

  getProfile: async (): Promise<User> => {
    const { data: response } = await client.query({
      query: GET_PROFILE,
      fetchPolicy: "network-only",
    });
    return response.profile;
  },

  editProfile: async (input: {
    name?: string;
    username?: string;
    email?: string;
    phoneNumber?: string;
  }): Promise<User> => {
    const { data: response } = await client.mutate({
      mutation: EDIT_PROFILE,
      variables: { input },
    });
    return response.editProfile;
  },

  changePassword: async (data: {
    currentPassword: string;
    newPassword: string;
  }): Promise<boolean> => {
    const { data: response } = await client.mutate({
      mutation: CHANGE_PASSWORD,
      variables: {
        currentPassword: data.currentPassword,
        newPassword: data.newPassword,
      },
    });
    return response.changePassword;
  },
};
